import {
    PropsWithChildren,
    JSX,
    useRef,
    useState,
    Children,
    ReactElement,
    useCallback,
    useEffect,
    cloneElement,
    MutableRefObject,
} from 'react';
import classNames from 'classnames';
import { getNormalizedScrollLeft } from 'normalize-scroll-left';
import { debounce } from 'lodash-unified';
import { TabsBarArrow } from '@bg-components/Tabs/TabsBarArrow';
import { SideScrollDirection } from '@bg-components/Tabs/enums/SideScrollDirection';
import { useImmutableState } from '@betgames/bg-tools';
import { scrollToTarget } from '@bg-components/Tabs/scrollToTarget';
import { sideScroll } from '@bg-components/Tabs/sideScroll';
import { TabsBarItem } from '@bg-components/Tabs/TabsBarItem';
import {
    WritingSystemDirection,
    CustomCssClassNames,
    EMPTY_SPACE,
    partnerSettingsStore,
    getDirection,
} from '@bg-shared';
import { TabsType } from './enums/TabsType';
import classes from './Tabs.module.scss';

enum ScrollPosition {
    START = 'start',
    END = 'end',
}

const getScrollPosition = (
    element: HTMLDivElement,
    direction: WritingSystemDirection,
): ScrollPosition => {
    if (!element) {
        return null;
    }

    const scrollLeft = getNormalizedScrollLeft(element, direction);

    if (scrollLeft <= 2) {
        return ScrollPosition.START;
    }

    // -2 is a scrolling threshold
    if (scrollLeft >= element.scrollWidth - element.clientWidth - 2) {
        return ScrollPosition.END;
    }

    return null;
};

interface IProps {
    onChange: (value: unknown) => void;
    className?: string;
    contentClassName?: string;
    defaultValue?: unknown;
    type?: TabsType;
    shrinkForBetslip?: boolean;
    ripple?: boolean;
    stretchButtons?: boolean;
    inLobby?: boolean;
    allTabSelected?: boolean;
}

export const Tabs = ({
    defaultValue = EMPTY_SPACE,
    type = TabsType.default,
    ...props
}: PropsWithChildren<IProps>): JSX.Element => {
    const { partnerSettings } = partnerSettingsStore;
    const ref = useRef<HTMLDivElement>();
    const dir = getDirection(partnerSettings.isRtl);
    const [arrows, updateArrows] = useImmutableState({
        showLeft: false,
        showRight: false,
    });
    const [value, setValue] = useState<unknown>(
        defaultValue || (Children.toArray(props.children)[0] as ReactElement).props.value,
    );

    const onTabClick = useCallback(
        (newValue: unknown): void => {
            setValue(newValue);
            props.onChange(newValue);
        },
        [props.onChange],
    );

    const toggleArrows = (): void => {
        if (!ref.current) {
            return;
        }

        const position = getScrollPosition(ref.current, dir);
        // -2 is a scrolling threshold
        const hasScroll = ref.current.clientWidth < ref.current.scrollWidth - 2;

        const showRight = hasScroll && position !== ScrollPosition.END;
        const showLeft = hasScroll && position !== ScrollPosition.START;

        updateArrows((draft) => {
            if (draft.showRight !== showRight) {
                draft.showRight = showRight;
            }

            if (draft.showLeft !== showLeft) {
                draft.showLeft = showLeft;
            }
        });
    };

    const setSideScroll = useCallback((direction: SideScrollDirection): void => {
        sideScroll(ref.current, direction);
    }, []);

    useEffect(() => {
        const debounceResize = debounce(toggleArrows, 300);

        // Init call should be with timeout as well to give time for DOM rerender
        debounceResize();

        ref.current.addEventListener('scroll', toggleArrows, true);
        window.addEventListener('resize', debounceResize, true);

        return () => {
            debounceResize.cancel();
            window.removeEventListener('resize', debounceResize, true);
        };
    }, []);

    useEffect(() => {
        toggleArrows();
    }, [Children.count(props.children)]);

    return (
        <div
            className={classNames(
                {
                    [classes.tabsDefault]: type === TabsType.default,
                    [classes.tabsSub]:
                        type === TabsType.subNavigation || type === TabsType.pickXNavigation,
                    [CustomCssClassNames.TABS_DEFAULT]: type === TabsType.default,
                    [CustomCssClassNames.TABS_SUB]:
                        type === TabsType.subNavigation || type === TabsType.pickXNavigation,
                    [classes.tabsLobby]: type === TabsType.lobby,
                },
                CustomCssClassNames.TABS,
                props.className,
            )}
        >
            <div
                className={classNames(classes.tabsBar, {
                    [classes.tabsBarShrinkForBetslip]: props.shrinkForBetslip,
                    [classes.lobby]: props.inLobby,
                    [classes.allTabSelected]: props.allTabSelected,
                })}
            >
                <TabsBarArrow
                    direction={SideScrollDirection.LEFT}
                    visible={arrows.showLeft}
                    onClick={setSideScroll}
                    ripple={props.ripple}
                    type={type}
                    isRtl={partnerSettings.isRtl}
                    inLobby={props.inLobby}
                />
                <div
                    className={classNames(classes.tabsBarScrollable, classes[type], {
                        [classes.rightArrowVisible]: arrows.showRight,
                        [classes.leftArrowVisible]: arrows.showLeft,
                        [classes.stretchButtons]: props.stretchButtons,
                    })}
                >
                    <div
                        className={classNames(classes.scrollableContent, props.contentClassName, {
                            [classes.lobby]: type === TabsType.lobby,
                            [classes.subTabs]:
                                type === TabsType.subNavigation ||
                                type === TabsType.pickXNavigation,
                        })}
                        ref={ref}
                    >
                        {Children.map(
                            Children.toArray(props.children).filter(
                                (child) => !!child,
                            ) as ReactElement[],
                            (child) => {
                                if (child.type !== TabsBarItem) {
                                    return child;
                                }

                                return cloneElement(child, {
                                    key: child.props.value,
                                    active: child.props.active ?? child.props.value === value,
                                    onClick: onTabClick,
                                    onActive: (
                                        itemRef: MutableRefObject<HTMLButtonElement>,
                                    ): void => {
                                        scrollToTarget(
                                            ref.current as HTMLElement,
                                            itemRef.current,
                                            false,
                                        );
                                    },
                                });
                            },
                        )}
                    </div>
                </div>
                <TabsBarArrow
                    direction={SideScrollDirection.RIGHT}
                    visible={arrows.showRight}
                    onClick={setSideScroll}
                    ripple={props.ripple}
                    type={type}
                    isRtl={partnerSettings.isRtl}
                    inLobby={props.inLobby}
                />
            </div>
        </div>
    );
};
